import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Col, Empty, Form, Input, List, message, Modal, Radio, Row, Space, Spin, Tabs, Tag, Typography} from "antd";
import {calculateTimeDifference} from "../../helpers/time";
import { useTranslation } from '../../hooks/useTranslation';
import { useIsPc } from '../../hooks/useIsPc';
import { formatDiscountLabel, getOriginalAmount, isDiscountedPrice } from './vipPricing';
import { useUserInfo } from '../../contexts/UserInfoContext';
import type { ImgCloudUserInfo } from '../../services/userInfo';

type PurchaseSlot = 'ONE_DAY' | 'ONE_WEEK' | 'ONE_MONTH' | 'ONE_YEAR';

interface PurchaseLink {
  id?: number;
  slot: PurchaseSlot;
  title: string;
  label?: string;
  url: string;
  pcOnly?: boolean;
  sortOrder?: number;
  enabled?: boolean;
}

interface VipPackage {
  id: string;
  days: number;
  amount: number;
  originalAmount?: number;
  discountPercent?: number;
  discounted?: boolean;
  desc: string;
  dailyUploadLimit?: number;
}

interface VipOrderHistory {
  source: 'WECHAT' | 'CARD';
  packageDesc?: string;
  days?: number;
  amountCents?: number | null;
  completedAt?: string | number | null;
}

interface WebPaySession {
  token: string;
  expiresAt: number;
  qrCodeDataUrl: string;
  miniProgramUrlLink?: string;
  packageInfo: VipPackage;
}

type VipCenterDefaultTab = 'card' | 'wechat';
type VipCenterTab = VipCenterDefaultTab | 'history';

interface VipCenterConfig {
  defaultTab: VipCenterDefaultTab;
  wechatDevelopingNoticeEnabled: boolean;
}

interface VipCenterConfigResponse extends Partial<VipCenterConfig> {
  isSuccess?: boolean;
}

const DEFAULT_VIP_CENTER_CONFIG: VipCenterConfig = {
  defaultTab: 'wechat',
  wechatDevelopingNoticeEnabled: false,
};

const CARD_PACKAGE_CONFIG: Record<
  PurchaseSlot,
  { packageId: string; titleKey: string; fallbackKey: string; suffixKey?: string }
> = {
  ONE_DAY: {
    packageId: '1d',
    titleKey: 'cart.oneDayMemberTitle',
    fallbackKey: 'cart.oneDayMember',
  },
  ONE_WEEK: {
    packageId: '7d',
    titleKey: 'cart.oneWeekMemberTitle',
    fallbackKey: 'cart.oneWeekMember',
  },
  ONE_MONTH: {
    packageId: '30d',
    titleKey: 'cart.oneMonthMemberTitle',
    fallbackKey: 'cart.oneMonthMember',
  },
  ONE_YEAR: {
    packageId: '365d',
    titleKey: 'cart.oneYearMemberTitle',
    fallbackKey: 'cart.oneYearMember',
    suffixKey: 'cart.oneYearContactSuffix',
  },
};

const formatCnyAmount = (amount?: number | string | null): string => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return '--';
  }
  return Number.isInteger(numeric)
    ? String(numeric)
    : numeric.toFixed(2).replace(/0$/, '').replace(/\.0$/, '');
};

const normalizeVipCenterDefaultTab = (defaultTab?: string): VipCenterDefaultTab =>
  defaultTab === 'wechat' ? 'wechat' : 'card';

const normalizeVipCenterConfig = (data?: Partial<VipCenterConfig>): VipCenterConfig => ({
  defaultTab: normalizeVipCenterDefaultTab(data?.defaultTab),
  wechatDevelopingNoticeEnabled:
    typeof data?.wechatDevelopingNoticeEnabled === 'boolean'
      ? data.wechatDevelopingNoticeEnabled
      : false,
});

const {Text} = Typography;

const Config: React.FC = () => {
  const { t } = useTranslation();
  const isPc = useIsPc();
  const { config, refresh, user } = useUserInfo();
  const userInfo: Partial<ImgCloudUserInfo> = user || {};
  const [cardNo, setCardNo] = useState('');
  const [cardPass, setCardPass] = useState('');
  const [extendCardNo, setExtendCardNo] = useState('');
  const [extendCardPass, setExtendCardPass] = useState('');
  const [payPackages, setPayPackages] = useState<VipPackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState('30d');
  const [qrLoading, setQrLoading] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrSession, setQrSession] = useState<WebPaySession | null>(null);
  const [qrStatus, setQrStatus] = useState('PENDING');
  const [qrCountdown, setQrCountdown] = useState(0);
  const [vipCenterConfig, setVipCenterConfig] = useState<VipCenterConfig>(DEFAULT_VIP_CENTER_CONFIG);
  const [activeTab, setActiveTab] = useState<VipCenterTab>(DEFAULT_VIP_CENTER_CONFIG.defaultTab);
  const hasManualTabChangeRef = useRef(false);
  const [orderHistory, setOrderHistory] = useState<VipOrderHistory[]>([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState('');
  const [purchaseLinks, setPurchaseLinks] = useState<Record<PurchaseSlot, PurchaseLink[]>>({
    ONE_DAY: [],
    ONE_WEEK: [],
    ONE_MONTH: [],
    ONE_YEAR: [],
  });

  const fetchPurchaseLinks = () => {
    fetch('/api/img-cloud/purchase-links')
      .then((res) => res.json())
      .then((res) => {
        if (res.success && res.data) {
          setPurchaseLinks((prev) => ({
            ...prev,
            ...res.data,
          }));
        }
      })
      .catch(() => {
        // 出错时使用默认配置兜底（当前硬编码逻辑）
      });
  };

  const fetchVipCenterConfig = () => {
    fetch('/pay/vip/center-config')
      .then((res) => res.json())
      .then((res: VipCenterConfigResponse) => {
        if (!res.isSuccess) {
          return;
        }
        const nextConfig = normalizeVipCenterConfig(res);
        setVipCenterConfig(nextConfig);
        if (!hasManualTabChangeRef.current) {
          setActiveTab(nextConfig.defaultTab);
        }
      })
      .catch(() => {
        // 配置获取失败时保留默认行为。
      });
  };

  const fetchPayPackages = () => {
    fetch('/pay/vip/packages')
      .then((res) => res.json())
      .then((res) => {
        if (res.isSuccess && Array.isArray(res.packages)) {
          setPayPackages(res.packages);
          if (res.packages.some((pkg: VipPackage) => pkg.id === '30d')) {
            setSelectedPackageId('30d');
          } else if (res.packages[0]) {
            setSelectedPackageId(res.packages[0].id);
          }
        }
      })
      .catch(() => {
        message.warning('微信支付套餐加载失败，请稍后重试');
      });
  };

  const fetchOrderHistory = () => {
    setOrderHistoryLoading(true);
    setOrderHistoryError('');
    fetch('/pay/vip/order-history')
      .then((res) => res.json())
      .then((res) => {
        if (!res.isSuccess || !Array.isArray(res.orders)) {
          throw new Error(res.message || t('cart.orderHistoryLoadFailed'));
        }
        setOrderHistory(res.orders);
      })
      .catch((error) => {
        setOrderHistory([]);
        setOrderHistoryError(error instanceof Error ? error.message : t('cart.orderHistoryLoadFailed'));
      })
      .finally(() => {
        setOrderHistoryLoading(false);
      });
  };

  useEffect(() => {
    fetchPurchaseLinks();
    fetchVipCenterConfig();
    fetchPayPackages();
  }, [])

  useEffect(() => {
    if (!qrModalOpen || !qrSession) {
      return;
    }
    const tickCountdown = () => {
      setQrCountdown(Math.max(0, Math.ceil((qrSession.expiresAt - Date.now()) / 1000)));
    };
    tickCountdown();
    const countdownTimer = window.setInterval(tickCountdown, 1000);
    const pollTimer = window.setInterval(() => {
      fetch(`/pay/vip/web-session/${encodeURIComponent(qrSession.token)}`)
        .then((res) => res.json())
        .then((res) => {
          if (!res.isSuccess) {
            return;
          }
          setQrStatus(res.status || 'PENDING');
          if (res.status === 'PAID') {
            window.clearInterval(pollTimer);
            window.clearInterval(countdownTimer);
            message.success(res.vipEndTime ? `支付成功，会员已开通至 ${res.vipEndTime}` : '支付成功，会员已开通');
            setQrModalOpen(false);
            setQrSession(null);
            void refresh();
          }
          if (res.status === 'EXPIRED') {
            window.clearInterval(pollTimer);
            setQrStatus('EXPIRED');
          }
        })
        .catch(() => {
          // 轮询失败时保持弹窗，下一轮继续尝试。
        });
    }, 2000);
    return () => {
      window.clearInterval(countdownTimer);
      window.clearInterval(pollTimer);
    };
  }, [qrModalOpen, qrSession, refresh])

  const [saving, setSaving] = useState(false)
  const [extending, setExtending] = useState(false)

  const handleTabChange = (key: string) => {
    if (key !== 'card' && key !== 'wechat' && key !== 'history') {
      return;
    }
    hasManualTabChangeRef.current = true;
    setActiveTab(key as VipCenterTab);
    if (key === 'history') {
      fetchOrderHistory();
    }
  };

  const createWebPaySession = () => {
    if (!userInfo.username) {
      message.warning('请先登录后再使用微信扫码支付');
      return;
    }
    if (!selectedPackageId) {
      message.warning('请选择会员套餐');
      return;
    }
    setQrLoading(true);
    fetch('/pay/vip/web-qrcode', {
      method: 'POST',
      body: JSON.stringify({packageId: selectedPackageId}),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      if (data.isSuccess) {
        setQrSession(data);
        setQrStatus('PENDING');
        if (!isPc) {
          if (data.miniProgramUrlLink) {
            window.location.href = data.miniProgramUrlLink;
          } else {
            message.error('暂时无法打开小程序，请在电脑端扫码支付');
          }
          return;
        }
        setQrModalOpen(true);
      } else {
        message.error(data.message || '生成微信支付码失败');
      }
    }).catch(() => {
      message.error(isPc ? '生成微信支付码失败，请稍后重试' : '打开小程序支付失败，请稍后重试');
    }).finally(() => {
      setQrLoading(false);
    });
  }

  const handleSave = () => {
    console.info('handleSave', config)
    setSaving(true);
    fetch('/login/active', {
      method: 'POST',
      body: JSON.stringify({
        cardNo,
        cardPass
      }),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('flag is', data);
      if (data.success || data.isSuccess) {
        message.info(data.message || t('cart.activateSuccess'))
        void refresh();
        setCardNo('');
        setCardPass('');
      } else {
        message.error(data.message || t('cart.activateFailed'))
      }
    }).finally(() => {
      setSaving(false);
    })
  }

  const handleExtend = () => {
    if (!extendCardNo.trim()) {
      message.error(t('cart.cardNoRequired'));
      return;
    }
    
    setExtending(true);
    fetch('/login/active', {
      method: 'POST',
      body: JSON.stringify({
        cardNo: extendCardNo,
        cardPass: extendCardPass
      }),
      headers: {
        'Content-Type': 'application/json;charset=utf8'
      }
    }).then(res => res.json()).then((data) => {
      console.info('extend result:', data);
      if (data.success || data.isSuccess) {
        message.success(data.message || t('cart.renewalSuccess'));
        void refresh();
        setExtendCardNo('');
        setExtendCardPass('');
      } else {
        message.error(data.message || t('cart.renewalFailed'))
      }
    }).finally(() => {
      setExtending(false);
    })
  }

  const renderLinks = (slot: PurchaseSlot) => {
    const links = purchaseLinks[slot] || [];
    if (!links.length) {
      return null;
    }

    return links
      .filter((link) => !link.pcOnly || isPc)
      .map((link, index) => {
        const prefix = link.label || `${index + 1}. `;
        const style: React.CSSProperties = {};
        if (index === 0 && isPc) {
          style.color = 'red';
        }
        return (
          <div key={link.id || `${slot}-${index}`}>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              style={style}
            >
              {prefix}{link.title}
            </a>
          </div>
        );
      });
  };

  const getPackageById = (packageId: string) => payPackages.find((pkg) => pkg.id === packageId);

  const formatDailyUploadLimit = (limit?: number | string | null) => {
    const numeric = Number(limit);
    if (!Number.isFinite(numeric)) {
      return '--';
    }
    if (numeric < 0) {
      return t('cart.dailyUploadUnlimited');
    }
    return t('cart.dailyUploadLimitValue', { count: numeric.toLocaleString() });
  };

  const getCurrentDailyUploadLimit = () => (
    userInfo.dailyMaxUploadCount ?? (config.dailyMaxUploadCount as number | string | undefined)
  );

  const renderCurrentDailyUploadLimit = () => {
    if (!userInfo.username || !userInfo.isActiveVip) {
      return null;
    }
    return (
      <Alert
        message={t('cart.currentDailyUploadLimit', {
          limit: formatDailyUploadLimit(getCurrentDailyUploadLimit()),
        })}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  const renderPackageDailyUploadLimit = (packageInfo?: VipPackage) => {
    if (!packageInfo) {
      return null;
    }
    return (
      <Tag color="blue">
        {t('cart.packageDailyUploadLimit', {
          limit: formatDailyUploadLimit(packageInfo.dailyUploadLimit),
        })}
      </Tag>
    );
  };

  const renderSlotDailyUploadLimit = (slot: PurchaseSlot) => {
    const packageInfo = getPackageById(CARD_PACKAGE_CONFIG[slot].packageId);
    if (!packageInfo) {
      return null;
    }
    return <div style={{ marginBottom: 8 }}>{renderPackageDailyUploadLimit(packageInfo)}</div>;
  };

  const renderMemberCardTitle = (slot: PurchaseSlot) => {
    const packageConfig = CARD_PACKAGE_CONFIG[slot];
    const packageInfo = getPackageById(packageConfig.packageId);
    if (!packageInfo) {
      return t(packageConfig.fallbackKey);
    }
    const title = t(packageConfig.titleKey);
    const amount = formatCnyAmount(getOriginalAmount(packageInfo));
    if (packageConfig.suffixKey) {
      return t('cart.memberPriceTitleWithSuffix', {
        title,
        amount,
        suffix: t(packageConfig.suffixKey),
      });
    }
    return t('cart.memberPriceTitle', { title, amount });
  };

  const renderWechatPackagePrice = (packageInfo: VipPackage) => {
    const discounted = isDiscountedPrice(packageInfo);
    return (
      <Space size={4} wrap>
        {discounted && (
          <Text delete type="secondary">¥{formatCnyAmount(getOriginalAmount(packageInfo))}</Text>
        )}
        <Text strong style={{ color: discounted ? '#cf1322' : undefined }}>
          ¥{formatCnyAmount(packageInfo.amount)}
        </Text>
        {discounted && isPc && <Tag color="red">微信支付 {formatDiscountLabel(packageInfo)}</Tag>}
      </Space>
    );
  };

  const renderWechatScanPay = () => {
    const selectedPackage = payPackages.find((pkg) => pkg.id === selectedPackageId) || payPackages[0];
    return (
      <Card
        title="微信扫码支付"
        style={{
          marginBottom: 16,
          overflow: 'hidden',
          border: '1px solid rgba(7, 193, 96, 0.22)',
          background: 'linear-gradient(135deg, #f2fff7 0%, #ffffff 46%, #fff8e8 100%)',
        }}
      >
        <Space direction="vertical" size="middle" style={{width: '100%'}}>
          <Alert
            type="success"
            showIcon
            message={isPc ? '推荐：微信扫码打开小程序，支付后自动开通当前 Web 账号' : '手机浏览器可直接打开小程序支付，支付后自动开通当前 Web 账号'}
          />
          <Radio.Group
            value={selectedPackageId}
            onChange={(e) => setSelectedPackageId(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            style={{display: 'flex', flexWrap: 'wrap', gap: 8}}
          >
            {payPackages.map((pkg) => {
              const dailyUploadLimit = formatDailyUploadLimit(pkg.dailyUploadLimit);
              return (
                <Radio.Button
                  key={pkg.id}
                  value={pkg.id}
                  style={{ marginBottom: 8, maxWidth: isPc ? undefined : '100%' }}
                >
                  {isPc ? (
                    <Space size={4} wrap>
                      <span>{pkg.desc} ·</span>
                      {renderWechatPackagePrice(pkg)}
                      <span>· {dailyUploadLimit}</span>
                    </Space>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%', minWidth: 0 }}>
                      <Text ellipsis={{ tooltip: pkg.desc }} style={{ flex: '1 1 auto', minWidth: 0 }}>
                        {pkg.desc}
                      </Text>
                      <span>·</span>
                      {renderWechatPackagePrice(pkg)}
                      <Text
                        ellipsis={{ tooltip: dailyUploadLimit }}
                        type="secondary"
                        style={{ flex: '0 1 8em', maxWidth: '8em', minWidth: 0 }}
                      >
                        · {dailyUploadLimit}
                      </Text>
                    </div>
                  )}
                </Radio.Button>
              );
            })}
          </Radio.Group>
          {selectedPackage && (
            <div>
              <Tag color="green">{selectedPackage.days} 天</Tag>
              {renderWechatPackagePrice(selectedPackage)}
              {renderPackageDailyUploadLimit(selectedPackage)}
              <Text type="secondary">将开通到：{userInfo.username || '请先登录'}</Text>
            </div>
          )}
          <Button type="primary" size="large" loading={qrLoading} onClick={createWebPaySession}>
            {isPc ? '生成微信小程序支付码' : '打开小程序支付'}
          </Button>
          {!isPc && (
            <Text type="secondary">
              如果无法自动跳转，请复制当前页面到系统浏览器后重试，或在电脑端使用微信扫码。
            </Text>
          )}
        </Space>
      </Card>
    );
  };

  const renderPayModal = () => (
    <Modal
      open={qrModalOpen}
      title="微信扫码支付"
      footer={null}
      width={420}
      onCancel={() => setQrModalOpen(false)}
      destroyOnClose
    >
      {qrSession && (
        <div style={{textAlign: 'center'}}>
          <div
            style={{
              margin: '8px auto 18px',
              padding: 18,
              width: 242,
              borderRadius: 28,
              background: 'radial-gradient(circle at 30% 20%, #c8ffe1 0, #ffffff 52%, #ffe7a6 100%)',
              boxShadow: '0 20px 50px rgba(25, 120, 68, 0.18)',
            }}
          >
            <img src={qrSession.qrCodeDataUrl} alt="微信小程序支付码" style={{width: 206, height: 206}}/>
          </div>
          <Space direction="vertical" size={8}>
            <Text strong>{qrSession.packageInfo.desc}</Text>
            {renderWechatPackagePrice(qrSession.packageInfo)}
            <Text type="secondary">
              {t('cart.packageDailyUploadLimit', {
                limit: formatDailyUploadLimit(qrSession.packageInfo.dailyUploadLimit),
              })}
            </Text>
            <Text type="secondary">用微信扫码打开小程序，完成支付后本页会自动刷新</Text>
            {qrStatus === 'EXPIRED' ? (
              <Tag color="red">二维码已过期，请重新生成</Tag>
            ) : (
              <Tag color="green">等待支付 · 剩余 {qrCountdown} 秒</Tag>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );

  const renderCardPurchase = () => (
    <Form>
      <Row gutter={16}>
        <Col span={8}>
          <Card title={renderMemberCardTitle('ONE_DAY')}>
            {renderSlotDailyUploadLimit('ONE_DAY')}
            {renderLinks('ONE_DAY')}
          </Card>
          <Card title={t('cart.xianyuStore')}>
            {renderLinks('ONE_DAY')}
          </Card>
        </Col>
        <Col span={8}>
          <Card title={renderMemberCardTitle('ONE_MONTH')}>
            {renderSlotDailyUploadLimit('ONE_MONTH')}
            {renderLinks('ONE_MONTH')}
          </Card>
          <br/>
          <Card title={renderMemberCardTitle('ONE_WEEK')}>
            {renderSlotDailyUploadLimit('ONE_WEEK')}
            {renderLinks('ONE_WEEK')}
          </Card>
          <Card title={renderMemberCardTitle('ONE_YEAR')}>
            {renderSlotDailyUploadLimit('ONE_YEAR')}
            {renderLinks('ONE_YEAR')}
            <img
              style={{width: "150px"}}
              src={"https://cdn.z.wiki/autoupload/20241217/W2xb/630X678/image.png"}
              alt={t('cart.oneYearMember')}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card title={t('cart.afterPurchase')}>
            <a target={"_blank"}
               rel="noreferrer"
               href={"https://cdn.z.wiki/autoupload/20241017/9UVf/744X1614/image.png"}>
              {t('cart.clickToEnlarge')}
              <img src={"https://cdn.z.wiki/autoupload/20241017/9UVf/744X1614/image.png"}
                   alt={t('cart.cardPasswordGuide')}
                   style={{maxWidth: '50%', margin: '0 auto'}}/>
            </a>
          </Card>
        </Col>
      </Row>
      <br/>
      <Alert type={'info'} message={t('cart.activateHint')}/>
      <br/>
      <Form.Item label={t('cart.cardNo')}>
        <Input placeholder={t('cart.cardNoPlaceholder')} value={cardNo}
               onChange={(e) => setCardNo(e.target.value)}/>
      </Form.Item>
      <Form.Item label={t('cart.password')}>
        <Input placeholder={t('cart.passwordPlaceholder')} value={cardPass}
               onChange={(e) => setCardPass(e.target.value)}/>
      </Form.Item>
      <Form.Item>
        <Button type="primary" loading={saving} onClick={handleSave}>{t('cart.activateMember')}</Button>
      </Form.Item>
    </Form>
  );

  const renderRenewal = () => (
    <Card title={t('cart.vipRenewal')} style={{ marginTop: 16 }}>
      <Alert
        type="info"
        message={t('cart.renewalHint')}
        style={{ marginBottom: 16 }}
      />
      <Form.Item label={t('cart.cardNo')}>
        <Input
          placeholder={t('cart.cardNoPlaceholder')}
          value={extendCardNo}
          onChange={(e) => setExtendCardNo(e.target.value)}
        />
      </Form.Item>
      <Form.Item label={t('cart.password')}>
        <Input
          placeholder={t('cart.passwordPlaceholder')}
          value={extendCardPass}
          onChange={(e) => setExtendCardPass(e.target.value)}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" loading={extending} onClick={handleExtend}>
          {t('cart.renewalMember')}
        </Button>
      </Form.Item>
    </Card>
  );

  const renderWechatTab = () => (
    <>
      {vipCenterConfig.wechatDevelopingNoticeEnabled && (
        <Alert
          type="warning"
          showIcon
          message="微信扫码支付开发中"
          description={isPc ? '电脑端使用微信扫码打开小程序；手机浏览器会直接拉起小程序支付页。' : '点击下方按钮打开小程序支付页，请确认账号、套餐和金额后再付款。'}
          style={{ marginBottom: 16 }}
        />
      )}
      {renderWechatScanPay()}
    </>
  );

  const formatOrderHistoryTime = (completedAt?: string | number | null) => {
    if (!completedAt) {
      return '-';
    }
    const date = new Date(completedAt);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  const renderOrderHistory = () => {
    if (orderHistoryLoading) {
      return <div style={{ padding: '32px 0', textAlign: 'center' }}><Spin /></div>;
    }
    if (orderHistoryError) {
      return (
        <Alert
          type="error"
          showIcon
          message={orderHistoryError}
          action={<Button size="small" onClick={fetchOrderHistory}>{t('cart.orderHistoryRetry')}</Button>}
        />
      );
    }
    if (!orderHistory.length) {
      return <Empty description={t('cart.orderHistoryEmpty')} />;
    }
    return (
      <List
        size="small"
        dataSource={orderHistory}
        renderItem={(order, index) => {
          const isWechatOrder = order.source === 'WECHAT';
          const hasAmount = order.amountCents !== null
            && order.amountCents !== undefined
            && Number.isFinite(Number(order.amountCents));
          return (
            <List.Item key={`${order.source}-${order.completedAt || 'unknown'}-${index}`}>
              <div style={{ width: '100%' }}>
                <Space wrap size={[8, 4]}>
                  <Tag color={isWechatOrder ? 'green' : 'blue'}>
                    {isWechatOrder ? t('cart.orderHistoryWechat') : t('cart.orderHistoryCard')}
                  </Tag>
                  <Text strong>{order.packageDesc || t('cart.orderHistoryVip')}</Text>
                  {order.days ? <Text type="secondary">{t('cart.orderHistoryDays', { days: order.days })}</Text> : null}
                </Space>
                <Space wrap size={[8, 4]} style={{ marginTop: 4 }}>
                  {isWechatOrder ? (
                    <Text type={hasAmount ? undefined : 'secondary'}>
                      {hasAmount
                        ? `¥${formatCnyAmount(Number(order.amountCents) / 100)}`
                        : t('cart.orderHistoryAmountUnavailable')}
                    </Text>
                  ) : null}
                  <Text type="secondary">{formatOrderHistoryTime(order.completedAt)}</Text>
                </Space>
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  const vipLevelLabel = userInfo.vipLevel
    ? t(`cart.vipLevels.${userInfo.vipLevel}`)
    : t('cart.vipMember');

  console.info('user is', userInfo);
  if (userInfo.isActiveVip) {
    // vipEndTime. 格式为 YYYY-MM-DD HH:mm:ss
    const ttl = calculateTimeDifference(userInfo.vipEndTime || '');
    const text = t('cart.vipActive', {
      level: vipLevelLabel,
      endTime: userInfo.vipEndTime || '-',
      ttl,
    });
    return (
      <>
        {renderPayModal()}
        <Alert message={text} type="success" style={{ marginBottom: 16 }}/>
        {renderCurrentDailyUploadLimit()}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            { key: 'card', label: '卡密续期', children: renderRenewal() },
            { key: 'wechat', label: '微信扫码支付', children: renderWechatTab() },
            { key: 'history', label: t('cart.orderHistory'), children: renderOrderHistory() },
          ]}
        />
      </>
    )
  }


  return (
    <>
      {renderPayModal()}
      {renderCurrentDailyUploadLimit()}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          { key: 'card', label: '卡密购买/激活', children: renderCardPurchase() },
          { key: 'wechat', label: '微信扫码支付', children: renderWechatTab() },
          { key: 'history', label: t('cart.orderHistory'), children: renderOrderHistory() },
        ]}
      />
    </>
  );
};

export default Config;
